import React from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { useHelpTextId } from '../../hooks/useHelpTextId'
import { getInputStyles } from '../Arcade/Input/arcade-input-styles'
import ArcadeHelpText, {
  ArcadeHelpTextComponentProps,
  combineAriaDescribedBy,
  extractHelpTextProps,
  getErrorIcon,
} from '../Arcade/Input/ArcadeHelpText'

export type ArcadeTextAreaProps = Pick<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  | 'value'
  | 'onChange'
  | 'placeholder'
  | 'defaultValue'
  | 'disabled'
  | 'name'
  | 'id'
  | 'minLength'
  | 'maxLength'
  | 'required'
  | 'aria-label'
  | 'aria-describedby'
  | 'aria-invalid'
  | 'aria-required'
  | 'autoFocus'
  | 'readOnly'
  | 'spellCheck'
  | 'autoComplete'
> &
  ArcadeHelpTextComponentProps & {
    shouldShowErrorIcon?: boolean
  }

const TextAreaContainer = styled.div<{ isInvalid: boolean }>`
  ${({ isInvalid }) =>
    isInvalid &&
    css`
      position: relative;
    `}
`

const StyledTextArea = styled.textarea<{
  isInvalid: boolean
  disabled: boolean
}>`
  display: block;
  width: 100%;
  min-height: 10rem;
  resize: none;

  ${({ theme }) => theme.typography.input};

  ${({ theme, isInvalid, disabled }) =>
    getInputStyles({ theme, hasLeadingIcon: false, hasTrailingIcon: false, isInvalid, disabled })}
`

const ArcadeTextArea = React.forwardRef<HTMLTextAreaElement, ArcadeTextAreaProps>((props, ref) => {
  const {
    isInvalid = false,
    shouldShowErrorIcon = true,
    disabled = false,
    required,
    defaultValue,
    'aria-describedby': ariaDescribedBy,
    ...restProps
  } = props

  const helpTextProps = extractHelpTextProps(props)
  const helpTextId = useHelpTextId(helpTextProps)

  return (
    <>
      <TextAreaContainer isInvalid={isInvalid}>
        <StyledTextArea
          aria-invalid={isInvalid}
          isInvalid={isInvalid}
          aria-required={required}
          aria-describedby={combineAriaDescribedBy(ariaDescribedBy, helpTextId)}
          disabled={disabled}
          defaultValue={defaultValue}
          ref={ref}
          {...restProps}
        />
        {getErrorIcon({ isInvalid, shouldShowErrorIcon, disabled, isTextArea: true })}
      </TextAreaContainer>
      <ArcadeHelpText helpTextId={helpTextId} {...helpTextProps} />
    </>
  )
})

ArcadeTextArea.displayName = 'ArcadeTextArea'

export default ArcadeTextArea
